// src/Contact.js
import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";

const Contact = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        message: "",
        file: null,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isMobileView, setIsMobileView] = useState(false);
    const fileInputRef = useRef(null);

    // Responsive layout check
    useEffect(() => {
        const checkResponsiveness = () => {
            setIsMobileView(window.innerWidth < 640);
        };

        // Initial check
        checkResponsiveness();

        // Add event listener for window resize
        window.addEventListener("resize", checkResponsiveness);

        // Cleanup listener
        return () => window.removeEventListener("resize", checkResponsiveness);
    }, []);

    const handleChange = (e) => {
        if (e.target.name === "file") {
            setFormData({ ...formData, file: e.target.files[0] });
        } else {
            setFormData({ ...formData, [e.target.name]: e.target.value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append("name", formData.name.trim());
            formDataToSend.append("email", formData.email.trim());
            formDataToSend.append("message", formData.message.trim());

            if (formData.file) {
                formDataToSend.append("file", formData.file);
            }

            // Build the URL dynamically using the environment variable
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/contact/`,
                {
                    method: "POST",
                    body: formDataToSend,
                }
            );

            if (!response.ok) {
                // Try to parse JSON error response, if available
                const errorData = await response.json();
                throw new Error(errorData.error || "Failed to send message");
            }

            Swal.fire({
                title: "Success!",
                text: "Your message and file have been sent successfully.",
                icon: "success",
                confirmButtonColor: "#3085d6",
                confirmButtonText: "OK",
            });

            // Clear form
            setFormData({ name: "", email: "", message: "", file: null });
        } catch (error) {
            console.error("Contact form error:", error);
            Swal.fire({
                title: "Oops!",
                text: error.message || "Something went wrong. Please try again later.",
                icon: "error",
                confirmButtonColor: "#d33",
                confirmButtonText: "OK",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle file button click
    const handleFileButtonClick = () => {
        fileInputRef.current.click();
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, []);

    return (
        <section
            id="contact"
            ref={sectionRef}
            className={`py-12 sm:py-16 bg-gray-100 dark:bg-gray-900 transition-all ${isVisible ? "animate-fadeIn" : "opacity-0"}`}
        >
            <div className="container mx-auto px-4 sm:px-6 md:px-12 lg:px-20 text-center">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-800 dark:text-white mb-4 sm:mb-6">
                    Contact Me
                </h2>

                {/* Contact Details */}
                <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6 text-gray-700 dark:text-gray-300 text-base sm:text-lg mb-6 sm:mb-8">
                    <p><span className="text-pink-500 text-xl">📍</span> <span className="font-medium">NERIMA, TOKYO, JAPAN</span></p>
                    <p><span className="text-pink-500 text-xl">📞</span> <span className="font-medium">+81 80-2373-6156</span></p>
                </div>

                {/* Contact Form - Improved for mobile interaction */}
                <form
                    onSubmit={handleSubmit}
                    className="w-full max-w-xl mx-auto bg-white dark:bg-gray-800 p-5 sm:p-6 rounded-lg shadow-md"
                >
                    <div className="mb-5">
                        <input
                            type="text"
                            name="name"
                            placeholder="Your Name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full h-12 px-4 py-2 text-base border border-gray-300 rounded-lg dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                            autoComplete="name"
                            style={{ fontSize: '16px' }}
                        />
                    </div>
                    <div className="mb-5">
                        <input
                            type="email"
                            name="email"
                            placeholder="Your Email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full h-12 px-4 py-2 text-base border border-gray-300 rounded-lg dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                            autoComplete="email"
                            style={{ fontSize: '16px' }}
                        />
                    </div>
                    <div className="mb-5">
                        <textarea
                            name="message"
                            placeholder="Your Message"
                            value={formData.message}
                            onChange={handleChange}
                            className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px]"
                            required
                            style={{ fontSize: '16px' }}
                        />
                    </div>
                    <div className="mb-5">
                        <div
                            className="flex items-center border border-gray-300 rounded-lg overflow-hidden dark:border-gray-600 dark:bg-gray-700"
                            onClick={handleFileButtonClick}
                        >
                            <span className="flex-shrink-0 cursor-pointer bg-blue-50 px-4 py-3 text-base font-semibold text-blue-700 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-100">
                                Choose File
                            </span>
                            <span className="px-3 text-base text-gray-500 dark:text-gray-300 truncate">
                                {formData.file ? formData.file.name : "No file chosen"}
                            </span>
                            <input
                                ref={fileInputRef}
                                id="file-upload"
                                type="file"
                                name="file"
                                onChange={handleChange}
                                className="hidden"
                            />
                        </div>
                    </div>
                    <button
                        type="submit"
                        className="w-full h-12 bg-blue-500 text-white px-4 rounded-lg text-lg font-medium active:bg-blue-700  relative z-30"
                        style={{ fontSize: '16px' }}
                    >
                        {isSubmitting ? "Sending..." : "Send Message"}
                    </button>
                </form>
            </div>
        </section>
    );
};

export default Contact;